/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { IOrigin } from "../../../../interfaces/origins";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useMounted from "../../../../hooks/mounted";
import { createOrigin, listOrigins, showOrigin, updateOrigin } from "@/services/activities/originsService";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: IOrigin = {
    id: 0,
    name: "",
    isActive: true,
};

export const useOrigins = ({
    disabledGetAll,
    disabledShowId,
    defaultLimit,
    filter: filterInitial,
    includes = [],
    originId,
}: {
    disabledGetAll?: boolean;
    disabledShowId?: boolean;
    defaultLimit?: number;
    filter?: Record<string, any>;
    includes?: string[];
    originId?: string;
}) => {
    const { idOrigin: id } = useParams<{ idOrigin: string }>();
    const location = useLocation();
    const [origins, setOrigins] = useState<IOrigin[]>([]);
    const [origin, setOrigin] = useState<IOrigin>(DEFAULT_DATA);
    const { filter, handleFilter, page, setPage, limit, setLimit } =
      useEntityQueryParams({ filter: filterInitial, defaultLimit });
    const [totalItems, setTotalItems] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { goAdd, goEdit, goBack, pathBack } = useEntityNavigation({ basePath: "/towermanagement/origins" });

    const handleOrigin = <K extends keyof IOrigin>(key: K, value: IOrigin[K]) => {
        setOrigin((current) => ({ ...current, [key]: value }));
    };

    const getAll = ({ page = 1, limit }: { page?: number; limit?: number }) => {
        setLoading(true);
        listOrigins({ includes: [...includes], filter, page, limit })
            .then(({ origins: listOriginsResult, limit, currentPage, totalItems }) => {
                setOrigins(listOriginsResult);
                
                setLimit(Number(limit));
                setTotalItems(Number(totalItems));
            })
            .finally(() => setLoading(false));
    };

    const search = useEntitySearch({ list: listOrigins, key: "origins", includes, filter });


    const get = ({ id }: { id: string | number }) => {
        setLoading(true);
        showOrigin({ id })
            .then(({ origin: originShow }) => setOrigin(originShow))
            .finally(() => setLoading(false));
    };

    const create = () => {
        setLoading(true);
        createOrigin({ origin })
            .then(() => {
                toast.success("Origem criada com sucesso!");
                navigate(pathBack)
            })
            .finally(() => setLoading(false));
    };

    const update = () => {
        if (!origin?.id) return;
        setLoading(true);
        updateOrigin({ id: origin.id, origin })
            .then(() => {
                toast.success("Origem atualizada com sucesso!");
                navigate(pathBack)
            })
            .finally(() => setLoading(false));
    };

    useMounted(() => {
        getAll({ page, limit });
    }, [page, limit, filter]);

    useEffect(() => {
        const isPage = location.pathname.includes("/towermanagement/origins");
        const targetId = originId || id;

        if (targetId && (isPage || originId) && !disabledShowId) {
            if (targetId !== "new") get({ id: targetId });
            return;
        }

        if (!disabledGetAll) getAll({ limit });
    }, [originId, id, disabledGetAll, disabledShowId]);

    return {
        origins,
        origin,
        loading,
        setPage,
        setLimit,
        page,
        limit,
        goEdit,
        goBack,
        goAdd,
        handleOrigin,
        create,
        id,
        update,
        totalItems,
        search,
        handleFilter,
    };
};
